import React, { useLayoutEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core";
import Description from "@material-ui/icons/Description";
import FileCopyOutlined from "@material-ui/icons/FileCopyOutlined";
import FormatListBulleted from "@material-ui/icons/FormatListBulleted";
import ImageOutlined from "@material-ui/icons/ImageOutlined";
import LocationOn from "@material-ui/icons/LocationOn";
import OpenInNew from "@material-ui/icons/OpenInNew";
import Phone from "@material-ui/icons/Phone";
import Reply from "@material-ui/icons/Reply";
import Videocam from "@material-ui/icons/Videocam";
import moment from "moment-timezone";
import { bareUrl, pal, usePal } from "./reviewUi";
import { realDestination } from "./caseText";
import chatBg from "../../static/chatbg.png";

// A flagged template as WhatsApp shows it (Meta's template preview). This is
// the customer's content, so WhatsApp colours are fine inside it. Content may
// be phishing: every URL and phone number is plain text, never a link.

const WA = {
  wallpaper: "#efeae2",
  bubble: "#ffffff",
  text: "#111b21",
  muted: "#667781",
  divider: "#e9edef",
  link: "#00a5f4",
  media: "#d1d7db",
  mediaIcon: "#8696a0",
};

const useStyles = makeStyles((theme) => {
  const c = pal(theme);
  return {
    wall: {
      background: `${WA.wallpaper} url(${chatBg}) center top / 380px auto repeat`,
      borderRadius: 8,
      padding: "14px 12px 14px 20px",
    },
    msg: { position: "relative", maxWidth: "100%" },
    card: {
      position: "relative",
      background: WA.bubble,
      borderRadius: 7.5,
      boxShadow: "0 1px .5px rgba(11,20,26,.13)",
    },
    tailed: {
      borderTopLeftRadius: 0,
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: -8,
        width: 8,
        height: 13,
        background: WA.bubble,
        clipPath: "polygon(0 0, 100% 0, 100% 100%)",
      },
    },
    content: { padding: "6px 7px 8px 9px", color: WA.text, fontSize: 13.6, lineHeight: "19px", wordBreak: "break-word" },
    media: {
      height: 120,
      margin: "-3px -4px 6px -6px",
      borderRadius: 6,
      background: WA.media,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 2,
      color: WA.mediaIcon,
      fontSize: 11.5,
      letterSpacing: ".02em",
    },
    mediaIcon: { fontSize: 40, color: WA.mediaIcon },
    header: { fontWeight: 700, fontSize: 14.5, marginBottom: 4, whiteSpace: "pre-wrap" },
    body: {
      whiteSpace: "pre-wrap",
      "& code": { fontFamily: "ui-monospace, Menlo, Consolas, monospace", fontSize: 12.5 },
    },
    clamped: {
      WebkitMaskImage: "linear-gradient(to bottom, #000 calc(100% - 38px), transparent)",
      maskImage: "linear-gradient(to bottom, #000 calc(100% - 38px), transparent)",
    },
    more: {
      cursor: "pointer",
      background: "none",
      border: 0,
      padding: 0,
      marginTop: 2,
      font: "inherit",
      fontSize: 13.6,
      color: WA.link,
      "&:hover": { textDecoration: "underline" },
    },
    footer: { color: WA.muted, fontSize: 12.5, marginTop: 4, whiteSpace: "pre-wrap" },
    time: { color: WA.muted, fontSize: 11, textAlign: "right", marginTop: 2, lineHeight: "15px" },
    button: { borderTop: `1px solid ${WA.divider}`, padding: "9px 10px", textAlign: "center" },
    buttonText: {
      color: WA.link,
      fontSize: 14,
      fontWeight: 500,
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      cursor: "default",
      maxWidth: "100%",
      wordBreak: "break-word",
    },
    seeAll: { cursor: "pointer", background: "none", border: 0, padding: 0, font: "inherit", fontWeight: 500 },
    icon: { fontSize: 16, flex: "none" },
    url: { fontFamily: "ui-monospace, Menlo, Consolas, monospace", fontSize: 11, marginTop: 3, wordBreak: "break-all", lineHeight: 1.4 },
    carousel: { display: "flex", gap: 8, overflowX: "auto", marginTop: 6, paddingBottom: 4 },
    slide: { flex: "0 0 190px", display: "flex" },
    slideCard: { flex: 1, display: "flex", flexDirection: "column", "& > :first-child": { flex: 1 } },
    empty: { color: c.hint, fontSize: 12.5, background: WA.bubble, borderRadius: 7.5, padding: "10px 12px" },
  };
});

// WhatsApp markup: *bold*, _italic_, ~strike~, ```mono```. Rendered as React
// nodes (never HTML); {{1}} placeholders stay visible as text. Each pattern is
// (prefix)(inner) so snake_case words don't turn italic.
const MARKS = [
  { re: /()```([\s\S]+?)```/, tag: "code" },
  { re: /()\*([^*\n]+)\*/, tag: "b" },
  { re: /(^|[\s(])_([^_\n]+)_(?![A-Za-z0-9])/, tag: "i" },
  { re: /()~([^~\n]+)~/, tag: "s" },
];
export function waFormat(text, key = "f") {
  const str = String(text || "");
  let best = null;
  MARKS.forEach((m) => {
    const hit = m.re.exec(str);
    if (hit) {
      const at = hit.index + hit[1].length;
      if (!best || at < best.at) best = { hit, m, at };
    }
  });
  if (!best) return str ? [str] : [];
  const { hit, m, at } = best;
  const inner = m.tag === "code" ? hit[2] : waFormat(hit[2], `${key}i`);
  return [
    str.slice(0, at),
    React.createElement(m.tag, { key }, inner),
    ...waFormat(str.slice(hit.index + hit[0].length), `${key}n`),
  ].filter((x) => x !== "");
}

const BUTTON_ICON = {
  URL: OpenInNew,
  PHONE_NUMBER: Phone,
  QUICK_REPLY: Reply,
  COPY_CODE: FileCopyOutlined,
  OTP: FileCopyOutlined,
  FLOW: FormatListBulleted,
};
const titleFor = (type) =>
  ({ URL: "Visit website", PHONE_NUMBER: "Call", QUICK_REPLY: "Quick reply", COPY_CODE: "Copy code", OTP: "Copy code" }[type] ||
  "Button");

function Button({ button }) {
  const s = useStyles();
  const c = usePal();
  const real = realDestination(button);
  const type = String(button?.type || "").toUpperCase();
  const Icon = BUTTON_ICON[type] || Reply;
  return (
    <div className={s.button}>
      <span className={s.buttonText}>
        <Icon className={s.icon} />
        {button?.text || titleFor(type)}
      </span>
      {real ? (
        <div className={s.url} style={{ color: c.error }} title={`Template link: ${button.url}`}>
          → {bareUrl(button.resolved_url)}
        </div>
      ) : button?.url ? (
        <div className={s.url} style={{ color: WA.muted }}>
          {bareUrl(button.url)}
        </div>
      ) : null}
      {button?.phone_number ? (
        <div className={s.url} style={{ color: WA.muted }}>
          {button.phone_number}
        </div>
      ) : null}
    </div>
  );
}

// Meta shows two buttons and "See all options" when there are more than three.
// Hidden real destinations still show, so nothing risky is folded away.
function Buttons({ buttons }) {
  const s = useStyles();
  const c = usePal();
  const [all, setAll] = useState(false);
  if (!buttons.length) return null;
  const fold = buttons.length > 3 && !all;
  const shown = fold ? buttons.slice(0, 2) : buttons;
  const hiddenReal = fold ? buttons.slice(2).filter(realDestination) : [];
  return (
    <>
      {shown.map((b, i) => (
        <Button key={i} button={b} />
      ))}
      {fold ? (
        <div className={s.button}>
          <button type="button" className={`${s.buttonText} ${s.seeAll}`} onClick={() => setAll(true)}>
            <FormatListBulleted className={s.icon} />
            See all options
          </button>
          {hiddenReal.map((b, i) => (
            <div key={i} className={s.url} style={{ color: c.error }} title={`Template link: ${b.url}`}>
              {b.text ? `${b.text} ` : ""}→ {bareUrl(b.resolved_url)}
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}

const MEDIA = {
  IMAGE: { Icon: ImageOutlined, word: "Image" },
  VIDEO: { Icon: Videocam, word: "Video" },
  DOCUMENT: { Icon: Description, word: "Document" },
  LOCATION: { Icon: LocationOn, word: "Location" },
};

const pick = (components) => {
  const list = Array.isArray(components) ? components : [];
  const byType = (t) => list.find((x) => String(x?.type || "").toUpperCase() === t);
  return {
    header: byType("HEADER"),
    body: byType("BODY"),
    footer: byType("FOOTER"),
    buttons: byType("BUTTONS")?.buttons || [],
    carousel: byType("CAROUSEL"),
  };
};

// Long bodies fold to `lines` lines (at most 320px) with "Show more"; the
// header, footer and buttons (and their real destinations) always show.
const LINE = 19;
function Body({ text, lines = 12 }) {
  const s = useStyles();
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [long, setLong] = useState(false);
  const max = Math.min(lines * LINE, 320);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && !open) setLong(el.scrollHeight > el.clientHeight + 1);
  }, [text, open, max]);
  return (
    <>
      <div ref={ref} className={`${s.body} ${long && !open ? s.clamped : ""}`} style={open ? undefined : { maxHeight: max, overflow: "hidden" }}>
        {waFormat(text)}
      </div>
      {long ? (
        <button type="button" className={s.more} aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          {open ? "Show less" : "Show more"}
        </button>
      ) : null}
    </>
  );
}

// One white card: header, body, footer, time, then the button rows.
function Card({ components, time, tail, className, lines }) {
  const s = useStyles();
  const { header, body, footer, buttons } = pick(components);
  const format = String(header?.format || "").toUpperCase();
  const media = format && format !== "TEXT" ? MEDIA[format] || { Icon: Description, word: format.toLowerCase() } : null;
  return (
    <div className={`${s.card} ${tail ? s.tailed : ""} ${className || ""}`}>
      <div className={s.content}>
        {media ? (
          <div className={s.media}>
            <media.Icon className={s.mediaIcon} />
            {media.word}
          </div>
        ) : null}
        {!media && header?.text ? <div className={s.header}>{waFormat(header.text)}</div> : null}
        {body?.text ? <Body text={body.text} lines={lines} /> : null}
        {footer?.text ? <div className={s.footer}>{footer.text}</div> : null}
        {time ? <div className={s.time}>{time}</div> : null}
      </div>
      <Buttons buttons={buttons} />
    </div>
  );
}

export default function TemplatePreview({ item }) {
  const s = useStyles();
  const template = item?.template;
  const fallback = item?.content?.text || item?.text;
  const time = item?.createdAt ? moment(item.createdAt).format("HH:mm") : "";
  const components = template?.components || [];
  const { carousel } = pick(components);
  let inner;
  if (components.length) {
    inner = (
      <>
        <div className={s.msg}>
          <Card components={components} time={time} tail />
        </div>
        {carousel ? (
          <div className={s.carousel}>
            {(carousel.cards || []).map((card, i) => (
              <div key={i} className={s.slide}>
                <Card components={card?.components} className={s.slideCard} lines={6} />
              </div>
            ))}
          </div>
        ) : null}
      </>
    );
  } else if (fallback) {
    inner = (
      <div className={s.msg}>
        <div className={`${s.card} ${s.tailed}`}>
          <div className={s.content}>
            <Body text={fallback} />
            {time ? <div className={s.time}>{time}</div> : null}
          </div>
        </div>
      </div>
    );
  } else {
    inner = <div className={s.empty}>No preview for this item</div>;
  }
  return <div className={s.wall}>{inner}</div>;
}
